import React, { useState, useEffect, useCallback, useMemo } from "react";
import "../assets/css/admin-dashboard.css";

/* programmes array (kept) */
const programmes = [
  "Biomedical Engineering (ND)",
  "Biomedical Engineering (HND)",
  "Anaesthetic Technicians",
  "School of Health Information Management (ND)",
  "School of Health Information Management (HND)",
  // "Department of Nursing Sciences",
  "Department of Nursing Sciences",
  "School of Medical and Psychiatry Social Work",
  "School of Community Health",
  "School of Community Health I",
  "School of Community Health II",
  "Accident & Emergency",
  "Critical Care Nursing",
  "Neonatal Nursing",
  "Nephrology Nursing",
  "Ophthalmic Nursing",
  "Paediatric Nursing",
  "Perioperative Nursing",
];

export default function AdminDashboard() {
  const [counts, setCounts] = useState({});
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("2026");
  const [loadingCounts, setLoadingCounts] = useState(false);
  const [loadingApps, setLoadingApps] = useState(false);
  const [apps, setApps] = useState([]); // from /api/checkapplicationinfo
  const [query, setQuery] = useState(""); // search programmes
  const [showTopN, setShowTopN] = useState(6);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await fetch("/api/biodata/years");
        if (!res.ok) throw new Error("failed fetching years");
        const data = await res.json();
        if (!mounted) return;
        const normalized = Array.isArray(data)
          ? data
              .map((y) => (y == null ? "" : String(y).trim()))
              .filter((y) => y !== "")
          : [];
        setYears(normalized);
        if (normalized.length > 0) {
          // if user had "all" selected, keep it; otherwise default to latest
          setSelectedYear((p) => (p === "all" ? "all" : normalized[0]));
        }
      } catch (err) {
        console.warn("years fetch failed", err);
        setYears([]);
        setSelectedYear("2026");
      }
    })();
    return () => (mounted = false);
  }, []);

  const loadCounts = useCallback(
    async (year) => {
      setLoadingCounts(true);
      const result = {};
      try {
        await Promise.all(
          programmes.map(async (prog) => {
            const params = new URLSearchParams({ programme: prog });
            if (year && year !== "all") params.set("year", String(year));
            const url = `/api/checkappbiodata?${params.toString()}`;
            const res = await fetch(url);
            const data = res.ok ? await res.json() : [];
            result[prog] = Array.isArray(data) ? data.length : 0;
          }),
        );
        setCounts(result);
      } catch (err) {
        console.error("Error loading counts:", err);
      } finally {
        setLoadingCounts(false);
      }
    },
    [
      /* stable */
    ],
  );

  useEffect(() => {
    loadCounts(selectedYear);
  }, [selectedYear, loadCounts]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoadingApps(true);
      try {
        const res = await fetch("/api/checkapplicationinfo");
        const data = res.ok ? await res.json() : [];
        if (!mounted) return;
        setApps(Array.isArray(data) ? data.slice(0, 12) : []);
      } catch (err) {
        console.error("Failed to fetch applicationinfo", err);
        setApps([]);
      } finally {
        setLoadingApps(false);
      }
    })();
    return () => (mounted = false);
  }, []);

  const totalApplicants = useMemo(
    () =>
      Object.values(counts).reduce(
        (s, v) => s + (typeof v === "number" ? v : 0),
        0,
      ),
    [counts],
  );

  const paymentSummary = useMemo(() => {
    const summary = { paid: 0, pending: 0, unknown: 0, total: 0 };
    for (const a of apps) {
      summary.total += 1;
      const status = (a.paymentStatus || "").toLowerCase();
      if (status.includes("paid")) summary.paid += 1;
      else if (status.includes("pending") || status.includes("unpaid"))
        summary.pending += 1;
      else summary.unknown += 1;
    }
    return summary;
  }, [apps]);

  const filteredProgrammes = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return programmes;
    return programmes.filter((p) => p.toLowerCase().includes(q));
  }, [query]);

  const topProgrammes = useMemo(() => {
    return Object.entries(counts)
      .map(([prog, cnt]) => ({ prog, cnt: typeof cnt === "number" ? cnt : 0 }))
      .sort((a, b) => b.cnt - a.cnt)
      .slice(0, showTopN);
  }, [counts, showTopN]);

  const exportCsv = () => {
    const rows = [["Programme", "Applicants", "Year"]];
    const yr = selectedYear === "all" ? "All" : selectedYear;
    for (const p of filteredProgrammes) {
      rows.push([p, counts[p] != null ? counts[p] : 0, yr]);
    }
    const csv = rows
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `programme_counts_${yr}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="admprog-dashboard">
      <div className="admprog-container">
        <main className="admprog-main">
          {/* Header */}
          <header className="admprog-header">
            <div>
              <h1 className="admprog-page-title">Admin Overview</h1>
              <div className="admprog-page-sub">
                Admissions dashboard — quick operational view
              </div>
            </div>

            <div className="admprog-controls">
              <div className="admprog-control-row">
                <label className="admprog-control-label">Year</label>
                <select
                  className="admprog-control-select"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                >
                  <option value="2026">2026</option>
                  <option value="2025">2025</option>
                  <option value="all">All</option>
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>

                <label className="admprog-control-label">Search</label>
                <input
                  placeholder="Search programmes..."
                  className="admprog-control-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />

                {/* <button
                  className="admprog-btn admprog-ghost"
                  onClick={() => {
                    setQuery("");
                    setSelectedYear("all");
                  }}
                >
                  Reset
                </button> */}

                <button
                  className="admprog-btn admprog-primary"
                  onClick={exportCsv}
                  title="Export visible programme counts"
                >
                  Export CSV
                </button>
              </div>

              <div className="admprog-kpi-row">
                <div className="admprog-kpi">
                  <div className="admprog-kpi-label">Total applicants</div>
                  <div className="admprog-kpi-value">
                    {loadingCounts ? "..." : totalApplicants}
                  </div>
                </div>

                <div className="admprog-kpi">
                  <div className="admprog-kpi-label">Paid (recent)</div>
                  <div className="admprog-kpi-value">
                    {loadingApps ? "..." : paymentSummary.paid}
                  </div>
                </div>

                <div className="admprog-kpi">
                  <div className="admprog-kpi-label">Pending (recent)</div>
                  <div className="admprog-kpi-value">
                    {loadingApps ? "..." : paymentSummary.pending}
                  </div>
                </div>

                <div className="admprog-kpi">
                  <div className="admprog-kpi-label">Loaded year</div>
                  <div className="admprog-kpi-value">
                    {selectedYear === "all" ? "All" : selectedYear}
                  </div>
                </div>
              </div>
            </div>
          </header>

          {/* Single-column content: Top programmes, then programmes grid, then recent applicants */}
          <section className="admprog-section admprog-top-section" >
            <h3 className="admprog-panel-title" >Top programmes</h3>
            <div className="admprog-topbars">
              {topProgrammes.length === 0 ? (
                <div className="admprog-empty">No data yet</div>
              ) : (
                topProgrammes.map(({ prog, cnt }) => {
                  const max = Math.max(
                    ...Object.values(counts).map((v) =>
                      typeof v === "number" ? v : 0,
                    ),
                    1,
                  );
                  const pct = Math.round((cnt / max) * 100);
                  return (
                    <div key={prog} className="admprog-topbar-row">
                      <div className="admprog-topbar-label" title={prog}>
                        {prog}
                      </div>
                      <div className="admprog-topbar-bar">
                        <div
                          className="admprog-topbar-fill"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <div className="admprog-topbar-count">{cnt}</div>
                    </div>
                  );
                })
              )}
            </div>
          </section>

          <section className="admprog-section admprog-cards-section">
            <h3 className="admprog-panel-title">Programmes</h3>
            <div className="admprog-cards-grid">
              {filteredProgrammes.map((prog) => {
                const cnt = counts[prog] != null ? counts[prog] : 0;
                const max = Math.max(
                  ...Object.values(counts).map((v) =>
                    typeof v === "number" ? v : 0,
                  ),
                  1,
                );
                const pct = Math.round((cnt / max) * 100);
                return (
                  <div
                    key={prog}
                    className="admprog-card admprog-programme-card"
                    title={prog}
                  >
                    <div className="admprog-card-head">
                      <div className="admprog-card-title">{prog}</div>
                      <div className="admprog-card-count">
                        {loadingCounts ? "..." : cnt}
                      </div>
                    </div>

                    <div className="admprog-prog-bar">
                      <div
                        className="admprog-prog-bar-fill"
                        style={{ width: `${pct}%` }}
                      />
                    </div>

                    <div className="admprog-card-footer">
                      <div className="admprog-card-label">Applicants</div>
                      <button
                        className="admprog-btn admprog-tiny"
                        onClick={() => {
                          window.location.href = `/applicants?programme=${encodeURIComponent(
                            prog,
                          )}&year=${encodeURIComponent(selectedYear)}`;
                        }}
                      >
                        View
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="admprog-section admprog-recent-section">
            <div className="admprog-panel-title-row">
              <h3 className="admprog-panel-title">Recent applicants</h3>
              <div className="admprog-panel-actions">
                <button
                  className="admprog-btn admprog-ghost"
                  onClick={() => (window.location.href = "/applicants")}
                >
                  Manage applicants
                </button>
              </div>
            </div>

            <div className="admprog-recent-list">
              {loadingApps ? (
                <div className="admprog-empty">Loading recent applicants…</div>
              ) : apps.length === 0 ? (
                <div className="admprog-empty">No recent applicants</div>
              ) : (
                <table className="admprog-recent-table" role="table">
                  <thead>
                    <tr>
                      <th>Application No</th>
                      <th>Name</th>
                      <th>Programme</th>
                      <th>Phone</th>
                      <th>Payment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apps.map((a) => (
                      <tr key={a.id || a.ApplicationNo}>
                        <td title={a.ApplicationNo || "-"}>
                          {a.ApplicationNo || "-"}
                        </td>
                        <td title={a.fullName || "-"}>{a.fullName || "-"}</td>
                        <td title={a.programme || "-"}>{a.programme || "-"}</td>
                        <td title={a.phone || "-"}>{a.phone || "-"}</td>
                        <td>
                          <span
                            className={`admprog-payment-pill ${
                              (a.paymentStatus || "")
                                .toLowerCase()
                                .includes("paid")
                                ? "paid"
                                : "pending"
                            }`}
                          >
                            {a.paymentStatus || "Unknown"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}
